import React, { useState } from 'react';
import { Field, useForm } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { Button, FieldTextInput, IconClose, Heading } from '../../components';
import WorkItemCard from '../WorkItemCard';

import css from './CustomFieldPastWorks.module.css';

// Function to fetch URL metadata using native fetch
const fetchUrlMetadata = async (url) => {
  if (!url || !url.startsWith('http')) {
    return null;
  }

  try {
    // In development, the API runs on port 3500
    const apiUrl = process.env.NODE_ENV === 'development'
      ? `http://localhost:3500/api/fetch-url-metadata?${new URLSearchParams({ url })}`
      : `/api/fetch-url-metadata?${new URLSearchParams({ url })}`;

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.success) {
      return data.metadata;
    }
    return null;
  } catch (error) {
    console.error('Error fetching URL metadata:', error);
    return null;
  }
};

const PastWorkItem = ({ name, index, onRemove, intl }) => {
  const [fetchingMetadata, setFetchingMetadata] = useState(false);
  const [metadataFetched, setMetadataFetched] = useState(false);
  const [autoFillEnabled, setAutoFillEnabled] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingMetadata, setPendingMetadata] = useState(null);
  const form = useForm();

  const handleUrlBlur = async (e, urlFieldProps) => {
    const url = e.target.value;
    urlFieldProps.input.onBlur(e);

    if (url && url.startsWith('http')) {
      setFetchingMetadata(true);
      setMetadataFetched(false);
      const fetchedMetadata = await fetchUrlMetadata(url);
      setFetchingMetadata(false);

      if (fetchedMetadata) {
        // Save metadata with the work item FIRST
        const metadataField = `${name}.metadata`;
        form.change(metadataField, fetchedMetadata);

        // Only auto-fill if enabled
        if (autoFillEnabled) {
          const titleField = `${name}.title`;
          const descField = `${name}.description`;
          const currentTitle = form.getFieldState(titleField)?.value;
          const currentDesc = form.getFieldState(descField)?.value;

          // Check if we need to confirm overwrite
          const titleDifferent = currentTitle && fetchedMetadata.title && currentTitle !== fetchedMetadata.title;
          const descDifferent = currentDesc && fetchedMetadata.description && currentDesc !== fetchedMetadata.description;

          if (titleDifferent || descDifferent) {
            // Show confirmation dialog
            setPendingMetadata(fetchedMetadata);
            setShowConfirmDialog(true);
          } else {
            // Auto-fill empty fields
            if (!currentTitle && fetchedMetadata.title) {
              form.change(titleField, fetchedMetadata.title);
            }
            if (!currentDesc && fetchedMetadata.description) {
              form.change(descField, fetchedMetadata.description);
            }

            setMetadataFetched(true);
            setTimeout(() => setMetadataFetched(false), 3000);
          }
        } else {
          // Auto-fill disabled, just show success for image
          setMetadataFetched(true);
          setTimeout(() => setMetadataFetched(false), 3000);
        }

        console.log('Metadata saved to form:', metadataField, fetchedMetadata);
      }
    } else if (!url) {
      // Clear metadata if URL is removed
      const metadataField = `${name}.metadata`;
      form.change(metadataField, null);
      setMetadataFetched(false);
    }
  };

  const handleConfirmOverwrite = () => {
    if (pendingMetadata) {
      const titleField = `${name}.title`;
      const descField = `${name}.description`;

      if (pendingMetadata.title) {
        form.change(titleField, pendingMetadata.title);
      }
      if (pendingMetadata.description) {
        form.change(descField, pendingMetadata.description);
      }

      setMetadataFetched(true);
      setTimeout(() => setMetadataFetched(false), 3000);
    }

    setShowConfirmDialog(false);
    setPendingMetadata(null);
  };

  const handleCancelOverwrite = () => {
    setShowConfirmDialog(false);
    setPendingMetadata(null);
    setMetadataFetched(true);
    setTimeout(() => setMetadataFetched(false), 3000);
  };

  return (
    <div className={css.workItem}>
      <div className={css.workItemHeader}>
        <div className={css.workItemNumber}>
          <span className={css.workItemIndex}>{index + 1}</span>
          <Heading as="h2" className={css.workItemTitle}>
            <FormattedMessage id="CustomFieldPastWorks.workItemTitle" />
          </Heading>
        </div>
        <button
          type="button"
          className={css.removeButton}
          onClick={onRemove}
          aria-label={intl.formatMessage({ id: 'CustomFieldPastWorks.removeAriaLabel' })}
        >
          <IconClose size="small" />
        </button>
      </div>

      <div className={css.workItemContent}>
        <div className={css.workItemFields}>
          <Field name={`${name}.url`}>
            {(fieldProps) => (
              <div className={css.urlFieldWrapper}>
                <FieldTextInput
                  id={`${name}.url`}
                  name={`${name}.url`}
                  type="text"
                  label={fetchingMetadata ? intl.formatMessage({ id: 'CustomFieldPastWorks.urlFetching' }) : metadataFetched ? intl.formatMessage({ id: 'CustomFieldPastWorks.urlSuccess' }) : intl.formatMessage({ id: 'CustomFieldPastWorks.urlLabel' })}
                  placeholder={intl.formatMessage({ id: 'CustomFieldPastWorks.urlPlaceholder' })}
                  onBlur={(e) => handleUrlBlur(e, fieldProps)}
                  className={css.modernInput}
                />
                {fetchingMetadata && <span className={css.loadingIndicator}><FormattedMessage id="CustomFieldPastWorks.fetchingMetadata" /></span>}
                {metadataFetched && <span className={css.successIndicator}><FormattedMessage id="CustomFieldPastWorks.metadataLoaded" /></span>}
                <div className={css.urlOptions}>
                  <label className={css.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={autoFillEnabled}
                      onChange={(e) => setAutoFillEnabled(e.target.checked)}
                      className={css.checkbox}
                    />
                    <span className={css.checkboxText}><FormattedMessage id="CustomFieldPastWorks.autoFillCheckbox" /></span>
                  </label>
                </div>
              </div>
            )}
          </Field>

          <FieldTextInput
            id={`${name}.title`}
            name={`${name}.title`}
            type="text"
            label={intl.formatMessage({ id: 'CustomFieldPastWorks.titleLabel' })}
            placeholder={fetchingMetadata ? intl.formatMessage({ id: 'CustomFieldPastWorks.titlePlaceholderLoading' }) : autoFillEnabled ? intl.formatMessage({ id: 'CustomFieldPastWorks.titlePlaceholderAutoFill' }) : intl.formatMessage({ id: 'CustomFieldPastWorks.titlePlaceholderManual' })}
            validate={value => value ? undefined : intl.formatMessage({ id: 'CustomFieldPastWorks.titleRequired' })}
            disabled={fetchingMetadata || autoFillEnabled}
            className={css.modernInput}
          />

          <FieldTextInput
            id={`${name}.description`}
            name={`${name}.description`}
            type="textarea"
            label={intl.formatMessage({ id: 'CustomFieldPastWorks.descriptionLabel' })}
            placeholder={fetchingMetadata ? intl.formatMessage({ id: 'CustomFieldPastWorks.descriptionPlaceholderLoading' }) : autoFillEnabled ? intl.formatMessage({ id: 'CustomFieldPastWorks.descriptionPlaceholderAutoFill' }) : intl.formatMessage({ id: 'CustomFieldPastWorks.descriptionPlaceholderManual' })}
            disabled={fetchingMetadata || autoFillEnabled}
            className={css.modernTextarea}
          />
        </div>

        <div className={css.previewColumn}>
          <div className={css.previewLabel}><FormattedMessage id="CustomFieldPastWorks.previewLabel" /></div>
          <Field name={`${name}.title`}>
            {({ input: titleInput }) => (
              <Field name={`${name}.description`}>
                {({ input: descInput }) => (
                  <Field name={`${name}.url`}>
                    {({ input: urlInput }) => (
                      <Field name={`${name}.metadata`}>
                        {({ input: metadataInput }) => (
                          <WorkItemCard
                            title={titleInput.value}
                            description={descInput.value}
                            url={urlInput.value}
                            metadata={metadataInput.value}
                            isPreview={true}
                            viewMode="card"
                          />
                        )}
                      </Field>
                    )}
                  </Field>
                )}
              </Field>
            )}
          </Field>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className={css.dialogOverlay}>
          <div className={css.confirmDialog}>
            <h3 className={css.dialogTitle}><FormattedMessage id="CustomFieldPastWorks.confirmOverwriteTitle" /></h3>
            <p className={css.dialogMessage}>
              <FormattedMessage id="CustomFieldPastWorks.confirmOverwriteMessage" />
            </p>
            <div className={css.dialogContent}>
              {pendingMetadata?.title && (
                <div className={css.dialogField}>
                  <strong><FormattedMessage id="CustomFieldPastWorks.newTitleLabel" /></strong>
                  <p>{pendingMetadata.title}</p>
                </div>
              )}
              {pendingMetadata?.description && (
                <div className={css.dialogField}>
                  <strong><FormattedMessage id="CustomFieldPastWorks.newDescriptionLabel" /></strong>
                  <p>{pendingMetadata.description}</p>
                </div>
              )}
            </div>
            <div className={css.dialogButtons}>
              <button
                type="button"
                onClick={handleCancelOverwrite}
                className={css.dialogButtonCancel}
              >
                <FormattedMessage id="CustomFieldPastWorks.keepCurrentButton" />
              </button>
              <button
                type="button"
                onClick={handleConfirmOverwrite}
                className={css.dialogButtonConfirm}
              >
                <FormattedMessage id="CustomFieldPastWorks.replaceButton" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomFieldPastWorks = props => {
  const { name, fieldConfig, formId, intl } = props;
  const { saveConfig } = fieldConfig || {};
  const { maxItems = 20 } = saveConfig || {};
  const label = fieldConfig?.saveConfig?.label || fieldConfig?.label;

  return (
    <div className={css.pastWorksContainer}>
      <h3 className={css.sectionTitle}>{label}</h3>

      <FieldArray name={name}>
        {({ fields }) => {
          // Log the current values to debug
          if (fields.value && fields.value.length > 0) {
            console.log('Current past works data:', fields.value);
          }

          return (
            <>
              <div className={css.worksList}>
                {fields.map((name, index) => (
                  <PastWorkItem
                    key={name}
                    name={name}
                    index={index}
                    onRemove={() => fields.remove(index)}
                    intl={intl}
                  />
                ))}
              </div>

              {fields.length < maxItems && (
                <button
                  type="button"
                  className={css.addButton}
                  onClick={(e) => {
                    e.preventDefault();
                    fields.push({ title: '', description: '', url: '', metadata: null });
                  }}
                >
                  <FormattedMessage id="CustomFieldPastWorks.addWork" />
                </button>
              )}

              {fields.length >= maxItems && (
                <p className={css.maxItemsMessage}>
                  <FormattedMessage
                    id="CustomFieldPastWorks.maxItemsReached"
                    values={{ max: maxItems }}
                  />
                </p>
              )}
            </>
          );
        }}
      </FieldArray>
    </div>
  );
};

CustomFieldPastWorks.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(CustomFieldPastWorks);